import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * BreadcrumbPortal — renders its children into the shared right-aligned actions
 * slot inside the global Breadcrumbs bar (`#breadcrumb-actions-slot`).
 *
 * This lets a page lift its primary page-level actions (e.g. "New workspace",
 * "Add source") up onto the breadcrumb row instead of spending a dedicated
 * full-width line on them — reclaiming vertical space without any hardcoding or
 * functional change. If the slot isn't mounted (routes that hide breadcrumbs),
 * nothing renders and the page simply shows no portalled actions.
 */
export default function BreadcrumbPortal({ children, slotId = "breadcrumb-actions-slot" }) {
    const [node, setNode] = useState(null);

    useEffect(() => {
        let cancelled = false;
        let tries = 0;
        const find = () => {
            if (cancelled) return;
            const el = document.getElementById(slotId);
            if (el) { setNode(el); return; }
            // The slot may paint a tick after this page mounts — retry briefly.
            if (tries++ < 20) setTimeout(find, 50);
        };
        find();
        return () => { cancelled = true; setNode(null); };
    }, [slotId]);

    if (!node) return null;
    return createPortal(children, node);
}
